import { useEffect, useState, useCallback, useMemo } from "react";
import { getFunnelStats, ENTITLEMENTS, type FunnelStats, type Entitlement } from "@/api/adminApi";
import { RangeSelector } from "@/components/RangeSelector";
import { useRange } from "@/hooks/useRange";
import styles from "./FunnelPage.module.css";

interface FunnelRow {
  label: string;
  eventType: string;
  // When true, the conversion percentage compares against the funnel's top row
  // (100% denominator) rather than the previous row. Used for branched funnels
  // where multiple second-tier rows are sibling outcomes from the same top row.
  compareToTop?: boolean;
  // When true, read this row's count from counts_new_users (per-onboarding-journey
  // filtered) instead of counts (all devices). Used for rows 6+ of the ONBOARDING
  // funnel after the sign-in cutover.
  newUserOnly?: boolean;
}

const PRIMARY_FUNNEL: FunnelRow[] = [
  { label: "PROJECT OPENED", eventType: "project_opened" },
  { label: "AI PANEL OPENED", eventType: "ai_panel_opened" },
  { label: "GENERATE TAPPED", eventType: "generate_tapped" },
  { label: "GENERATE SUCCEEDED", eventType: "generate_succeeded" },
  { label: "DEPLETION SHOWN", eventType: "depletion_shown" },
  // pack_purchased is sourced server-side from token_purchases (distinct buyers
  // in range), not from user_events, and is not a downstream of depletion_shown.
  // compareToTop puts the denominator on project_opened where the comparison is
  // meaningful (buyers / openers).
  { label: "PACK PURCHASED (buyers)", eventType: "pack_purchased", compareToTop: true },
];

// Step labels above are generic on purpose: the step names that used to sit here were the
// 3.3.4 Quick Orient tour, and 3.6.0 runs several tours (orient, example, example_project,
// script_orient, import_checklist, characters, canvas_orient) that all report through the same
// step_index. Split by tour (metadata.tour) if needed.

const ENTITLEMENT_LABELS: Record<string, string> = {
  trial: "Trial",
  lifetime: "Lifetime",
  yearly: "Yearly",
  monthly: "Monthly",
  free: "Free (lapsed)",
  unknown: "Unknown (older build, or store not ready)",
};

// New User Onboarding — the comprehensive new-user journey from purchasing
// to tutorial completion. Rows 1-5 dedup on distinct device_id across the
// range. Row 5 is the cutover ("new account created"). Rows 6+ are filtered
// via newUserOnly to count only devices that produced a signin_succeeded
// with is_new_user=true in range (per-onboarding-journey semantics).
const ONBOARDING_FUNNEL: FunnelRow[] = [
  { label: "PURCHASING VIEW MOUNTED", eventType: "purchasing_view_mounted" },
  { label: "TAPPED START FREE TRIAL", eventType: "purchasing_free_trial_tapped", compareToTop: true },
  { label: "TAPPED A PAID PLAN", eventType: "purchasing_plan_tapped", compareToTop: true },
  { label: "TAPPED RESTORE", eventType: "purchasing_restore_tapped", compareToTop: true },
  { label: "DISMISSED PURCHASING (X)", eventType: "purchasing_closed", compareToTop: true },
  { label: "SIGN-IN VIEW APPEARED", eventType: "signin_view_mounted" },
  { label: "TAPPED SIGN IN WITH APPLE", eventType: "signin_provider_tapped", compareToTop: true },
  { label: "SKIPPED SIGN IN", eventType: "signin_skipped", compareToTop: true },
  { label: "NEW ACCOUNT CREATED", eventType: "signin_succeeded_new_user" },
  { label: "WELCOME VIEW APPEARED", eventType: "welcome_view_mounted", newUserOnly: true },
  { label: "CHOSE “VIEW EXAMPLE”", eventType: "welcome_choice_example", newUserOnly: true, compareToTop: true },
  { label: "CHOSE “START CREATING”", eventType: "welcome_choice_create", newUserOnly: true, compareToTop: true },
  { label: "EXAMPLE PROJECT AUTO-OPENED", eventType: "project_opened_auto_opened", newUserOnly: true },
  { label: "TIMELINE VIEW MOUNTED (auto-opened)", eventType: "timeline_view_mounted_auto_opened", newUserOnly: true },
  { label: "TUTORIAL STEP 1 SHOWN", eventType: "tutorial_step_shown_0", newUserOnly: true },
  { label: "TUTORIAL STEP 2 SHOWN", eventType: "tutorial_step_shown_1", newUserOnly: true },
  { label: "TUTORIAL STEP 3 SHOWN", eventType: "tutorial_step_shown_2", newUserOnly: true },
  { label: "TUTORIAL STEP 4 SHOWN", eventType: "tutorial_step_shown_3", newUserOnly: true },
  { label: "TUTORIAL STEP 5 SHOWN", eventType: "tutorial_step_shown_4", newUserOnly: true },
  { label: "TUTORIAL COMPLETED", eventType: "tutorial_completed", newUserOnly: true, compareToTop: true },
  { label: "TUTORIAL SKIPPED", eventType: "tutorial_skipped", newUserOnly: true, compareToTop: true },
];

const TUTORIAL_FUNNEL: FunnelRow[] = [
  { label: "STEP 1 SHOWN", eventType: "tutorial_step_shown_0" },
  { label: "STEP 2 SHOWN", eventType: "tutorial_step_shown_1" },
  { label: "STEP 3 SHOWN", eventType: "tutorial_step_shown_2" },
  { label: "STEP 4 SHOWN", eventType: "tutorial_step_shown_3" },
  { label: "STEP 5 SHOWN", eventType: "tutorial_step_shown_4" },
  { label: "TUTORIAL COMPLETED", eventType: "tutorial_completed", compareToTop: true },
  { label: "TUTORIAL SKIPPED", eventType: "tutorial_skipped", compareToTop: true },
];

interface RenderedRow {
  label: string;
  count: number;
  widthPct: number;              // visual bar width as % of funnel's top-of-funnel count
  conversionPct: number | null;  // null for the first row
}

function renderFunnel(
  rows: FunnelRow[],
  counts: Record<string, number>,
  countsNewUsers: Record<string, number>,
): RenderedRow[] {
  if (rows.length === 0) return [];
  const pickCount = (r: FunnelRow): number => {
    const map = r.newUserOnly ? countsNewUsers : counts;
    return map[r.eventType] ?? 0;
  };
  const top = pickCount(rows[0]!);
  return rows.map((r, i) => {
    const count = pickCount(r);
    const denom = r.compareToTop === true
      ? top
      : (i === 0 ? null : pickCount(rows[i - 1]!));
    const conversion = denom == null ? null : (denom === 0 ? 0 : (count / denom) * 100);
    const widthPct = top === 0 ? 0 : (count / top) * 100;
    return { label: r.label, count, widthPct, conversionPct: conversion };
  });
}

function Funnel({ title, caption, rows }: { title: string; caption?: string; rows: RenderedRow[] }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {caption && <p className={styles.caption}>{caption}</p>}
      <div className={styles.rows}>
        {rows.map((row, i) => (
          <div key={i} className={styles.row}>
            <span className={styles.label}>{row.label}</span>
            <div className={styles.barWrap}>
              <div className={styles.bar} style={{ width: `${row.widthPct}%` }} />
            </div>
            <span className={styles.count}>{row.count.toLocaleString()}</span>
            <span className={styles.conv}>
              {row.conversionPct == null ? "100%" : `${row.conversionPct.toFixed(1)}%`}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function FunnelPage() {
  const [rangeDays, setRangeDays] = useRange(30);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [stats, setStats] = useState<FunnelStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (days: number, ent: Entitlement | null) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFunnelStats(days, ent);
      setStats(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load funnel");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(rangeDays, entitlement); }, [rangeDays, entitlement, load]);

  const counts = stats?.counts ?? {};
  const countsNewUsers = stats?.counts_new_users ?? {};
  const primaryRows = useMemo(() => renderFunnel(PRIMARY_FUNNEL, counts, countsNewUsers), [counts, countsNewUsers]);
  const onboardingRows = useMemo(() => renderFunnel(ONBOARDING_FUNNEL, counts, countsNewUsers), [counts, countsNewUsers]);
  const tutorialRows = useMemo(() => renderFunnel(TUTORIAL_FUNNEL, counts, countsNewUsers), [counts, countsNewUsers]);

  const hasData = Object.values(counts).some((n) => n > 0);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Funnel</h1>
        <RangeSelector value={rangeDays} onChange={setRangeDays} />
        <div className={styles.rangeSelector} role="group" aria-label="Entitlement">
          <button
            type="button"
            className={`${styles.rangeButton} ${entitlement === null ? styles.rangeButtonActive : ""}`}
            onClick={() => setEntitlement(null)}
          >
            All
          </button>
          {ENTITLEMENTS.map((e) => (
            <button
              key={e}
              type="button"
              className={`${styles.rangeButton} ${entitlement === e ? styles.rangeButtonActive : ""}`}
              onClick={() => setEntitlement(e)}
            >
              {ENTITLEMENT_LABELS[e]}
            </button>
          ))}
        </div>
      </div>
      <p className={styles.caption}>
        Every count is distinct devices in the range, embedded app only (standalone web sends no
        telemetry). Entitlement is what the App Store receipt said at the moment of each event; it
        is reported by 3.6.0 and later, and only once the receipt has loaded, so earlier builds and
        events fired before the store was ready land in &ldquo;Unknown&rdquo;. Supabase holds no
        subscription state of its own, so these are the only numbers that can tell a trialist from a
        subscriber from a lapsed free-mode user.
      </p>
      {/* The strip describes the unfiltered population (under a filter it would repeat the top
          row). by_entitlement is guarded so an admin-api deployed before it existed cannot crash
          the page. */}
      {stats?.by_entitlement && !stats.entitlement && Object.keys(stats.by_entitlement).length > 0 && (
        <div className={styles.population}>
          <span className={styles.populationLabel}>Project opened, by entitlement:</span>
          {Object.entries(stats.by_entitlement)
            .sort(([, a], [, b]) => b - a)
            .map(([k, n]) => (
              <span key={k} className={styles.populationItem}>
                {ENTITLEMENT_LABELS[k] ?? k} <strong>{n.toLocaleString()}</strong>
              </span>
            ))}
        </div>
      )}

      {loading && <div className={styles.loading}>Loading...</div>}
      {error && <div className={styles.error}>{error}</div>}

      {!loading && !error && !hasData && (
        <div className={styles.empty}>
          No events in range. Verify telemetry clients are deployed and emitting events.
        </div>
      )}

      {!loading && !error && hasData && (
        <>
          <Funnel
            title={entitlement ? `PRIMARY CONVERSION — ${ENTITLEMENT_LABELS[entitlement]}` : "PRIMARY CONVERSION — all entitlements"}
            caption={
              "Project opened → AI panel → Generate → success → depletion → buyer. Unfiltered, this mixes " +
              "lapsed free-mode users (who can open projects but cannot generate) into the AI-panel step. " +
              (stats?.pack_purchased_source === "events"
                ? "PACK PURCHASED is from the client-emitted pack_purchased event because purchases cannot be attributed to an entitlement; it undercounts."
                : "PACK PURCHASED is distinct buyers from token_purchases in the range (authoritative), compared against project openers.")
            }
            rows={primaryRows}
          />
          <Funnel
            title="NEW USER ONBOARDING"
            caption="Rows through NEW ACCOUNT CREATED are all devices; rows after it count only devices whose sign-in created a new account in the range."
            rows={onboardingRows}
          />
          <Funnel
            title="TUTORIAL DROP-OFF (all users)"
            caption="All tours share these step events (orient, example, example project, script orient, import checklist, characters, canvas orient), so a step number here is not one specific screen."
            rows={tutorialRows}
          />
        </>
      )}
    </div>
  );
}
